'use client'

import { useState } from 'react'
import { CMSLink } from '@/components/Link'
import { ChevronsRight } from 'lucide-react'
import { Media } from '@/components/Media'
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel'
import { cn } from '@/utilities/ui'
import GalleryBlock2 from './Gallery2'
import RichText from '@/components/RichText'
import Autoplay from 'embla-carousel-autoplay'

export const GallaryComponent = (props: any) => {
  const { images, title, subtitle, eyebrow, hasCarousel, type, galleryCta, hasCta } = props
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState<any>(null)

  const openModal = (image: any) => {
    setSelectedImage(image)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setSelectedImage(null)
  }

  // Close modal on click outside image
  const handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal()
    }
  }

  if (type === 'galleryBlock2') return <GalleryBlock2 {...props} />

  return (
    <section className="w-full" aria-label="Gallery">
      <div className="container mx-auto 2xl:px-0 rounded-xl">
        <header className="flex flex-col items-center justify-center">
          {eyebrow && (
            <div className="border border-primary px-4 py-1 rounded-full mb-2 w-fit flex items-center justify-center">
              <span className="text-primary">
                <RichText data={eyebrow} enableGutter={false} enableProse={false} />
              </span>
            </div>
          )}
          {title && (
            <span className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-2">
              <RichText data={title} enableGutter={false} enableProse={false} />
            </span>
          )}
          {subtitle && (
            <span className="max-w-lg text-muted-foreground mx-auto text-center text-base lg:text-lg break-words">
              <RichText data={subtitle} enableGutter={false} enableProse={false} />
            </span>
          )}
        </header>
        {/* ...existing code... */}
        {!hasCarousel && (
          <div className="py-2">
            <ul
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 w-full gap-8"
              role="list"
            >
              {images?.map((image: any, index: any) => (
                <li key={index} className="w-full" role="listitem">
                  <figure className="group transition-transform duration-300 hover:scale-105">
                    <div
                      className="overflow-hidden rounded-xl cursor-pointer"
                      onClick={() => openModal(image)}
                    >
                      {image?.image &&
                        (() => {
                          let src: string | undefined = undefined
                          if (typeof image.image === 'string') src = image.image
                          else if (image.image && typeof image.image.url === 'string')
                            src = image.image.url
                          if (typeof src === 'string' && src.length > 0) {
                            return (
                              <Media
                                resource={image?.image}
                                imgClassName="w-full h-64 sm:h-64 md:h-80 lg:h-80 xl:h-96 rounded-xl object-cover transition-transform duration-300 group-hover:scale-110"
                                fetchPriority="low"
                                loading="lazy"
                              />
                            )
                          }
                          return null
                        })()}
                    </div>
                    {image?.caption && (
                      <div className="mt-3 text-sm text-foreground">
                        <RichText data={image.caption} enableGutter={false} />
                      </div>
                    )}
                  </figure>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* ...existing code... */}
        {hasCarousel && (
          <div className=" py-2">
            <Carousel
              plugins={[
                Autoplay({
                  delay: 2000,
                  stopOnInteraction: false,
                  stopOnMouseEnter: true,
                  stopOnFocusIn: true,
                }),
              ]}
              opts={{
                loop: true,
              }}
              aria-label="Gallery Carousel"
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {images?.map((image: any, index: any) => (
                  <CarouselItem
                    key={index}
                    className="pl-4 md:basis-1/2 lg:basis-1/3 xl:basis-1/4"
                    role="listitem"
                  >
                    <div className="p-2">
                      <figure className="group transition-transform duration-300 hover:scale-105">
                        <div
                          className="overflow-hidden rounded-xl cursor-pointer"
                          onClick={() => openModal(image)}
                        >
                          {image?.image &&
                            (() => {
                              let src: string | undefined = undefined
                              if (typeof image.image === 'string') src = image.image
                              else if (image.image && typeof image.image.url === 'string')
                                src = image.image.url
                              if (typeof src === 'string' && src.length > 0) {
                                return (
                                  <Media
                                    resource={image?.image}
                                    imgClassName="w-full h-64 sm:h-64 md:h-80 lg:h-80 xl:h-96 rounded-xl object-cover transition-transform duration-300 group-hover:scale-110"
                                    fetchPriority="low"
                                    loading="lazy"
                                  />
                                )
                              }
                              return null
                            })()}
                        </div>
                        {image?.caption && (
                          <div className="mt-3 text-sm text-center font-medium text-foreground">
                            <RichText
                              data={image.caption}
                              enableGutter={false}
                              enableProse={false}
                            />
                          </div>
                        )}
                      </figure>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        )}
        {/* CTA button below block */}
        {hasCta && (
          <nav className="flex flex-1 justify-center mt-10" aria-label="Gallery call to action">
            <div className="relative overflow-hidden group rounded-full inline-flex">
              <CMSLink
                {...galleryCta}
                className="z-10 inline-flex items-center border border-primary bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer font-semibold text-base tracking-wide transition-colors duration-300 rounded-full px-6 py-8  whitespace-normal text-center"
                aria-label={galleryCta?.label || 'View Gallery'}
              >
                <div className="w-12 h-12 rounded-full flex flex-1 items-center justify-center ml-2 bg-primary text-primary-foreground">
                  <ChevronsRight />
                </div>
              </CMSLink>
            </div>
          </nav>
        )}
        {/* Modal for big image */}
        {modalOpen && selectedImage && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 transition-opacity duration-300"
            onClick={handleModalClick}
          >
            <div
              className="relative max-w-3xl w-full mx-4 flex flex-col items-center justify-center"
              style={{ maxHeight: '90vh' }}
            >
              <button
                className="absolute top-2 right-2 bg-background rounded-full p-2 shadow-lg text-primary  focus:outline-none"
                onClick={closeModal}
                aria-label="Close"
              >
                &#10005;
              </button>
              <Media
                resource={selectedImage?.image}
                imgClassName="rounded-xl object-contain w-auto max-h-[70vh] mx-auto bg-white"
                fetchPriority="low"
                loading="lazy"
              />
              {selectedImage?.caption && (
                <div className="mt-4 text-center text-background">
                  <RichText data={selectedImage.caption} enableGutter={false} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
